import { BaseRate, BaseRateRowRecord } from "models/ratecard"
import { MediaField, MediaType } from "routes/Base/Admin/GlobalMediaTypes/types"
import {
  NON_REQUIRED_RATE_CARD_DIMENSIONS,
  getAppliedPercentageOrFixed,
  getDimensionEqualityCheck,
  getMediaField,
  getMediaFieldBySemantic,
  getMediaFieldValue,
  parseTime,
  roundHalfEven,
  setMediaFieldValue,
} from "./utils"

import { ChangedPlanRecord } from "../../types"
import { FieldsKey } from "./types"
import { calculationContext } from "./calculations"

export function calculateSystemFields(li: ChangedPlanRecord) {
  const withRateCardCost = calculateRateCardCost(li)
  const withUnitPrice = calculateUnitPrice(withRateCardCost)
  const withUnitCost = calculateUnitCost(withUnitPrice)

  return withUnitCost
}

// unit cost and price only if empty
function calculateUnitCost(li: ChangedPlanRecord): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  if (!fields) return li

  const unitPriceField = getMediaFieldBySemantic("unitPrice")
  const unitCostField = getMediaFieldBySemantic("unitCost")

  if (!unitPriceField || !unitCostField) return li

  // if unit cost already has a value do not re-calculate
  const unitCost = getMediaFieldValue<number>(fields, unitCostField)
  if (!!unitCost || unitCost === 0) return li

  const unitPrice = getMediaFieldValue<number>(fields, unitPriceField)
  if (!unitPrice) return li

  const discountFieldIds = cc.media?.mediaFields
    ?.filter((mf) => mf.semanticCode === "discount")
    ?.map((df) => df.id)

  const discountField = cc.media?.mediaFields?.find((mf) =>
    discountFieldIds?.includes(mf.id),
  )

  const discount = getMediaFieldValue<number>(fields, discountField)

  const discountSubtraction =
    !discountField || !discount
      ? 0
      : getAppliedPercentageOrFixed(
          unitPrice,
          Number(discount),
          discountField.format,
        )

  const updatedUnitCost = roundHalfEven(Number(unitPrice) - discountSubtraction)

  const withUnitCost = setMediaFieldValue(
    updatedUnitCost,
    fields,
    unitCostField,
  )

  return { ...li, [cc.fieldsKey]: withUnitCost }
}

function calculateUnitPrice(li: ChangedPlanRecord): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const unitPriceField = getMediaFieldBySemantic("unitPrice")
  if (!unitPriceField) return li

  const rateCardCostField = getMediaFieldBySemantic("rateCardCost")
  if (!rateCardCostField) return li

  // if unitPrice already has a value do not re-calculate
  const unitPrice = getMediaFieldValue<number>(fields, unitPriceField)
  if (!!unitPrice) return li

  const rateCardCostValue = getMediaFieldValue<number>(
    fields,
    rateCardCostField,
  )

  const withUnitPrice = setMediaFieldValue(
    rateCardCostValue,
    fields,
    unitPriceField,
  )

  return { ...li, [cc.fieldsKey]: withUnitPrice }
}

function calculateRateCardCost(li: ChangedPlanRecord): ChangedPlanRecord {
  const cc = calculationContext
  const fields = li[cc.fieldsKey] as ChangedPlanRecord[typeof cc.fieldsKey]

  const field = getMediaFieldBySemantic("rateCardCost")
  const duration = getMediaFieldBySemantic("duration")
  const programStartTimeField = getMediaFieldBySemantic("programStartTime")
  const programStartTime = getMediaFieldValue<string>(
    fields,
    programStartTimeField as MediaField,
  )
  const extraFields = {
    start: parseTime(programStartTime),
  }

  // guards
  if (!field) return li

  if (!fields) return li

  if (!duration) return li

  if (!cc.baseRate) return li

  if (li.type !== "line_item" && li.type !== "package") return li

  /*
    Get the base rate's enabled dimensions and produce a map like so:
    { semantic: [field.id, value] }
    Make sure all dimensions (plus duration) of the base rate exist in the map
    and then call the getBaseRateCost to try to match the filled in dimensions
    with a base rate cost. If one matches then set the Rate Card Cost field.
  */
  const enabledDimensions = cc.baseRate?.dimensions?.filter((d) => d.enabled)
  if (!enabledDimensions?.length) return li

  const dimensionFieldsMap =
    getDimensionsFieldMap(fields, cc.media, cc.baseRate) || {}

  const requiredDimensions = enabledDimensions?.filter(
    (d) =>
      !NON_REQUIRED_RATE_CARD_DIMENSIONS.includes(d.mediaFieldSemanticCode),
  )

  const dimensionsAreMissing = !requiredDimensions.every((ed) =>
    Object.keys(dimensionFieldsMap).includes(ed.mediaFieldSemanticCode),
  )

  if (dimensionsAreMissing) return li

  const matchedBaseRate = getBaseRateMatch(
    cc.baseRate,
    dimensionFieldsMap,
    extraFields,
  )

  const [, durationValue] = dimensionFieldsMap["duration"] ?? []
  let rateCardCost = !durationValue
    ? undefined
    : matchedBaseRate?.duration?.[durationValue]

  let withRateCardCost = setMediaFieldValue(rateCardCost, fields, field)

  /* apply the timebelt of the matched base rate to the line item */
  const matchedTimebelt = matchedBaseRate?.["timebelt"]
  const timebeltField = getMediaField("timebelt")

  if (!!matchedTimebelt && !!timebeltField) {
    withRateCardCost = setMediaFieldValue(
      matchedTimebelt,
      withRateCardCost,
      timebeltField,
    )
  }

  /*
    apply the loadings and the rate card cost as defaultCost
    to all variableCost fields that have no value already
  */
  cc.applyLoadings = true
  const variableCostFields = cc.media?.mediaFields?.filter(
    (mf) => mf.type === "VariableCost",
  )

  variableCostFields?.forEach((vcf) => {
    if (!fields[vcf.id]) {
      withRateCardCost = setMediaFieldValue(rateCardCost, withRateCardCost, vcf)
    }
  })

  return { ...li, [cc.fieldsKey]: withRateCardCost }
}

function getBaseRateMatch(
  baseRate: BaseRate,
  dimensionFieldsMap: Record<string, [string, any]>,
  extraFields: {
    start: string | undefined
  },
): BaseRateRowRecord | undefined {
  const matched = matchRateCostByDimensions(
    baseRate,
    dimensionFieldsMap,
    extraFields,
  )

  if (!matched) return undefined

  const [, durationValue] = dimensionFieldsMap["duration"] ?? []

  if (!durationValue) return undefined

  return matched
}

function matchRateCostByDimensions(
  baseRate: BaseRate,
  dimensionFieldsMap: Record<string, [string, any]>,
  extraFields: {
    start: string | undefined
  },
) {
  const matched: BaseRateRowRecord[] | undefined = baseRate.data?.filter(
    (brd) => {
      return Object.keys(brd)
        .filter((k) => !["id", "duration"].includes(k))
        .every((k) => {
          const predicateFn = getDimensionEqualityCheck(k)
          const [, value] = dimensionFieldsMap[k] ?? []

          return predicateFn(brd, value, k, extraFields)
        })
    },
  )

  return matched?.[0]
}

function getDimensionsFieldMap(
  fields: ChangedPlanRecord[FieldsKey],
  media?: MediaType,
  baseRate?: BaseRate,
): Record<string, [string, any]> {
  if (!fields) return {}

  const dimensionFields = Object.keys(fields).filter((fk) => {
    const mediaField = media?.mediaFields?.find((mf) => mf.id === fk)
    const hasValue = !!fields[fk]
    const hasSemantic = !!mediaField?.semanticCode
    const isDimensioned = baseRate?.dimensions?.find(
      (dm) =>
        dm.enabled && dm.mediaFieldSemanticCode === mediaField?.semanticCode,
    )

    return hasSemantic && isDimensioned && hasValue
  })

  const dimensionFieldsMap: Record<string, [string, any]> =
    dimensionFields?.reduce((dmap, df) => {
      const mediaField = media?.mediaFields?.find((mf) => mf.id === df)
      const semantic = mediaField?.semanticCode
      const value = getMediaFieldValue<any>(fields, mediaField)

      if (!mediaField || !semantic || !value) return dmap

      return { ...dmap, [semantic]: [mediaField.id, value] }
    }, {})

  return dimensionFieldsMap
}
